/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {
  DeviceTypeCategory,
  DownloadJobTask
} from '@ion/core/src/types/entities';

/**
 * Defines the `DownloadJobTask` fileTypes which are to use custom searches
 * for their matching `DeviceTypeCategory`; e.g. "LMU Config"
 */
export const TaskFileType = {
  Config : 'Config'
};

/**
 * Provides the pre-defined list of task types which are to custom search
 * implementations.
 */
const types: Partial<DownloadJobTask>[] = [{
  deviceType : DeviceTypeCategory.LMU,
  fileType   : TaskFileType.Config
}];

/**
 * Provides a utility which allows for determining if the given Device File
 * task type is to have custom search capabilities provided.
 *
 * ```typescript
 *   import JobTaskSearchUtils, {
 *     isCustomSearchTask
 *   } from './JobTaskSearchUtils';
 *   ...
 *
 *   {isExternalSearch(row.task) ? (
 *     <JobTaskSearchChooser type={row.task} ... /> :
 *     <JobTaskFileNameChooser ... />
 *   )}
 * ```
 *
 * @param   task the `DownloadJobTask` which is to be evaluated for custom
 *          search enablement.
 * @returns `true` if the given value within the `taskValue` argument matches
 *          a pre-defined custom search value, otherwise, `false`.
 */
export const isCustomSearchTask = (task: DownloadJobTask): boolean => (
  task ? types.some(({deviceType, fileType}: Partial<DownloadJobTask>) => (
    task.deviceType === deviceType && task.fileType === fileType
  )) : false
);

/**
 * Provides a utility for determining if the given `DownloadJobTask` is an LMU
 * Config.
 *
 * @param   task The `DownloadJobTask` which is to be determined if it is an
 *          LMU Config task.
 * @returns `true` if the provided `task` is an LMU Config `DownloadJobTask`,
 *          otherwise, returns `false`.
 */
export const isLMUConfigTask = ({deviceType, fileType}: DownloadJobTask = {}): boolean => (
  deviceType === DeviceTypeCategory.LMU && fileType === TaskFileType.Config
);
